/**
 * ApprovalStep.js
 *
 * Step 6: Capture approval/sign-off for audit trail.
 */

import React, { useRef, useState, useEffect } from 'react';
import { User, FileText, Pen, Check, AlertCircle, Info } from 'lucide-react';
import { CHECKLIST_TYPE_CONFIG } from '../../../../../../config/operations/checklistImportConfig';

const ApprovalStep = ({
  approvalData,
  onUpdateApproval,
  checklistType,
  itemCount
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const typeConfig = CHECKLIST_TYPE_CONFIG[checklistType];

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Restore existing signature if any
    if (approvalData?.signature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = approvalData.signature;
    }
  }, [approvalData?.signature]);

  // Drawing handlers
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      // Save signature to data
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      onUpdateApproval({ ...approvalData, signature: signatureData });
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onUpdateApproval({ ...approvalData, signature: null });
  };

  // Update form fields
  const handleFieldChange = (field, value) => {
    onUpdateApproval({ ...approvalData, [field]: value });
  };

  // Validation
  const isValid = approvalData?.approverName && approvalData?.approverRole && hasSignature;

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-slate-800">Approval Required</h3>
        <p className="text-sm text-slate-500 mt-1">
          Sign off on importing {itemCount} items to <span className="font-medium">{typeConfig?.label}</span>
        </p>
      </div>

      {/* Approval info banner */}
      <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-cyan-800">
            <p className="font-medium">Why is approval needed?</p>
            <p className="mt-1">
              Changes to SMS checklists require documented approval for regulatory compliance.
              Your signature will be recorded in the audit trail.
            </p>
          </div>
        </div>
      </div>

      {/* Approver details */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            <User className="h-4 w-4 inline mr-2" />
            Approver Name
          </label>
          <input
            type="text"
            value={approvalData?.approverName || ''}
            onChange={(e) => handleFieldChange('approverName', e.target.value)}
            placeholder="Enter your full name"
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            <FileText className="h-4 w-4 inline mr-2" />
            Role / Position
          </label>
          <select
            value={approvalData?.approverRole || ''}
            onChange={(e) => handleFieldChange('approverRole', e.target.value)}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            <option value="">Select your role</option>
            <option value="master">Master / Skipper</option>
            <option value="dpa">DPA</option>
            <option value="owner">Owner / Operator</option>
            <option value="manager">Fleet Manager</option>
            <option value="safety_officer">Safety Officer</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Document reference (optional) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            SMS Document Reference (Optional)
          </label>
          <input
            type="text"
            value={approvalData?.documentRef || ''}
            onChange={(e) => handleFieldChange('documentRef', e.target.value)}
            placeholder="e.g., SMS-PRO-001 Rev 3"
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Signature pad */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          <Pen className="h-4 w-4 inline mr-2" />
          Digital Signature
        </label>

        <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-slate-50">
          <canvas
            ref={canvasRef}
            width={400}
            height={120}
            className="w-full bg-white rounded border border-slate-200 touch-none cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />

          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-slate-500">
              {hasSignature ? 'Signature captured' : 'Sign above using mouse or touch'}
            </p>
            {hasSignature && (
              <button
                onClick={clearSignature}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Validation status */}
      <div className="mt-6">
        {isValid ? (
          <div className="flex items-center gap-2 text-green-600">
            <Check className="h-5 w-5" />
            <span className="text-sm font-medium">Ready to proceed</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">Complete all fields and add your signature</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalStep;
